import { bem } from '@socialive/common';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import Button from '../../Atoms/Button';
import Image from '../../Atoms/Image';
import CheckedIcon from '../../Icons/CheckedIcon';
import PlusIcon from '../../Icons/PlusIcon';
import Timestamp from '../../Utilities/Timestamp';
import PopConfirm from '../PopConfirm';
import ProgressBar from '../ProgressBar';
import './slideUpload.css';
import SubmitTextInput from './SubmitTextInput';

/*
#tailwind
*/

function SlideUpload({
  titleInputDisabled,
  titleInputContext,
  titleInputValue,
  onTitleInputChange,
  onTitleInputBlur,
  time,
  src,
  progress,
  className,
  onDelete,
  onUpdate,
  onRetry,
  added,
  onAdd,
  onRemove,
  onError,
  uploading,
  processing,
  actionButtonText,
  ...other
}) {
  const progressBarContext = useMemo(() => {
    if (onError) {
      return 'error';
    }
    if ((uploading && progress < 100) || processing) {
      return 'loading';
    }
    return 'success';
  }, [onError, progress, processing, uploading]);

  const displayProgress = useMemo(() => (!!onError && !progress ? 100 : Math.floor(progress)), [onError, progress]);

  return (
    <div className={bem`slideUpload ${className}`} {...other}>
      <div className="slideUpload__previewContainer">
        <Image src={src} alt={`${titleInputValue} preview`} />
      </div>
      <div className="slideUpload__content">
        <div className="slideUpload__input">
          <SubmitTextInput
            context={titleInputContext}
            mode="subtle"
            size="large"
            disabled={titleInputDisabled}
            placeholder="Enter a title"
            value={titleInputValue}
            onChange={onTitleInputChange}
            onBlur={onTitleInputBlur}
          />
        </div>
        <div className="slideUpload__meta">
          <Timestamp time={time} format="yyyy-MM-dd h:mm:ss a" />
          {onDelete && (
            <>
              &nbsp;&bull;&nbsp;
              <PopConfirm
                onConfirmButtonClick={onDelete}
                title="Are you sure you want to delete these slides? They may have been added to several broadcasts."
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
                confirmButtonContext="danger"
              >
                <Button size="small" mode="text" context="subtle" className="slideUpload__secondaryButton">
                  Delete
                </Button>
              </PopConfirm>
            </>
          )}
          {onUpdate && (
            <>
              &nbsp;&bull;&nbsp;
              <Button
                disabled={!src}
                onClick={onUpdate}
                size="small"
                mode="text"
                context="subtle"
                className="slideUpload__secondaryButton"
              >
                Update
              </Button>
            </>
          )}
        </div>
        {(uploading || onError || processing) && (
          <ProgressBar progress={displayProgress} hasIcon context={progressBarContext} />
        )}
        <div className="slideUpload__labelContainer">
          {uploading && progress < 100 && !onError && !processing && (
            <div className="slideUpload__label">{displayProgress}% uploaded</div>
          )}
          {!onError && processing && <div className="slideUpload__label">Processing...</div>}
          {uploading && progress === 100 && !onError && !processing && <div className="slideUpload__label">Ready</div>}
          {onError && (
            <div className="slideUpload__label">
              Error uploading: &nbsp;
              <Button
                size="small"
                mode="text"
                context="subtle"
                onClick={onError}
                className="slideUpload__secondaryButton"
              >
                Contact Support
              </Button>
              {onRetry && (
                <>
                  &nbsp;&bull;&nbsp;
                  <Button
                    size="small"
                    mode="text"
                    context="subtle"
                    onClick={onRetry}
                    className="slideUpload__secondaryButton"
                  >
                    Retry
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {!uploading && !processing && !onError && !added && (
          <Button context="subtle" onClick={onAdd} className="slideUpload__button ">
            <PlusIcon size="1rem" className="flex" />
            Add to Broadcast
          </Button>
        )}
        {!uploading && !processing && !onError && added && (
          <Button context="subtle" onClick={onRemove} className="slideUpload__button ">
            <CheckedIcon size="1rem" className="flex" />
            Added to Broadcast
          </Button>
        )}
      </div>
    </div>
  );
}

SlideUpload.propTypes = {
  titleInputDisabled: PropTypes.bool,
  titleInputValue: PropTypes.string,
  titleInputContext: PropTypes.oneOf(['default', 'error']),
  onTitleInputChange: PropTypes.func,
  onTitleInputBlur: PropTypes.func,
  time: PropTypes.number,
  src: PropTypes.node,
  progress: PropTypes.number,
  className: PropTypes.string,
  onDelete: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  onUpdate: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  onRetry: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  onError: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  added: PropTypes.bool,
  uploading: PropTypes.bool,
  processing: PropTypes.bool,
  actionButtonText: PropTypes.string,
};

SlideUpload.defaultProps = {
  titleInputDisabled: false,
  titleInputContext: 'default',
  titleInputValue: undefined,
  onTitleInputChange: undefined,
  onTitleInputBlur: undefined,
  time: undefined,
  src: undefined,
  progress: 0,
  className: undefined,
  onDelete: undefined,
  onUpdate: undefined,
  onRetry: undefined,
  onAdd: undefined,
  onRemove: undefined,
  onError: undefined,
  added: false,
  uploading: false,
  processing: false,
  actionButtonText: null,
};

export default SlideUpload;
